
import React from "react";
import * as Hooks from "../../../../../hooks";
import { i18nManager } from "../../../../../i18n";
import { Schemes } from "../../../../../utils";
import * as ReactRouterDom from "react-router-dom";
import { ConfirmDialog } from "../../../../shared/ConfirmDialog";
import { CreateTournamentSectionProps, CreateTournamentSection } from "../CreateTournament";
import { Button, ButtonManageRef, Checkbox, Dropdown, Form, FormControl, ObjectHelper, Scheme, TextAreaInput, TextInput } from "@rivtn/noseur";
import { BaseService, CountryService, ICountry, ITournamentEdition, SanitizedResponse, TournamentService } from "../../../../../services";
import { Elements } from "../../../../shared/Elements";

function Details(props: CreateTournamentSectionProps) {

    const [themeMode, _] = Hooks.useThemeMode();
    const countryService = CountryService.getInstance();
    const { active, profile, tournamentEditionState } = props;
    const [tournament, setTournament] = tournamentEditionState;
    const tournamentService = TournamentService.getInstance();
    const [countries, setCountries] = React.useState<ICountry[]>();
    const labels = i18nManager.Labels.host.create_tournament.details;
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [templateTournaments, setTemplateTournaments] = React.useState<{ label: string; items: ITournamentEdition[] }[]>();

    React.useEffect(fetchInformations, []);

    return (<div className="create-tournament-f flow details" ref={props.ref}>
        {!active && (<div className="overlay" onClick={() => props.setActiveSection(CreateTournamentSection.DETAILS, undefined, true)} />)}
        <span className="title">{labels.title}</span>
        <Form className="content" scheme={Schemes.OMNEHOST}>
            <Dropdown fill scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.copy_existing_tournament, }}
                options={templateTournaments} popoverHeaderTemplate={() => (<div style={{ margin: 10 }}>
                    <FormControl isFieldset fill label={labels.search}>
                        <TextInput fill onInputComplete={(value) => {
                            queryTemplateTournaments(value);
                        }} />
                    </FormControl>
                </div>)} optionGroupTemplate={(option) => (<span style={{ fontSize: 14, fontWeight: "normal" }}>{option.label}</span>)}
                optionTemplate={(option: ITournamentEdition) => (<div style={{ display: "flex", flexDirection: "row" }}>
                    <img alt={option.name} src={option.logo_url} style={{ width: 40, height: 40, marginRight: 10, borderRadius: "100%" }} />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: 500, marginBottom: 2 }}>{option.name}</span>
                        <span style={{ fontSize: 14 }}>{labels.last_organized} {option.dates.registration_start_date}</span>
                    </div>
                </div>)} onSelectOption={(option) => {
                    props.copyFromExistingTournamentEdition(option);
                }} />
            <Checkbox label={labels.is_recurring} defaultChecked={tournament.details_settings?.is_recurring} onChecked={(is_recurring) => {
                setTournament({ ...tournament, details_settings: { ...tournament.details_settings as any, is_recurring } });
            }} />
            <FormControl isFieldset fill label={labels.name}>
                <TextInput fill defaultValue={tournament.name} onInputComplete={(name) => {
                    setTournament({ ...tournament, name });
                }} />
            </FormControl>
            <div style={{ display: "flex" }}>
                <Dropdown fill options={countries} optionMap={{ label: "name", value: "code" }} selectedOptionIndex={countries?.findIndex(c => c.code === (tournament.country_code ?? profile.country_code))}
                    formControlProps={{ isFieldset: true, label: labels.country }}
                    scheme={Schemes.OMNEHOST} onSelectOption={(country: ICountry) => {
                        setTournament({ ...tournament, country_code: country.code });
                    }} />
                <Dropdown scheme={Schemes.OMNEHOST} fill options={i18nManager.Labels.tournaments.filter.form.find((f) => f.name === "type")?.options}
                    formControlProps={{ style: { marginLeft: 10 }, isFieldset: true, label: labels.location }}
                    selectedOptionIndex={i18nManager.Labels.tournaments.filter.form.find((f) => f.name === "type")?.options?.findIndex(c => c.value === tournament.location)}
                    onSelectOption={(option: any) => {
                        setTournament({ ...tournament, location: option.value });
                    }} />
            </div>
            <FormControl isFieldset fill label={labels.description} >
                <TextAreaInput fill defaultValue={tournament.description} style={{ height: 200 }} onInputComplete={(description) => {
                    setTournament({ ...tournament, description });
                }} />
            </FormControl>
            <FormControl isFieldset fill label={labels.physical_location}>
                <TextInput fill defaultValue={tournament.physical_address_location} onInputComplete={(physical_address_location) => {
                    setTournament({ ...tournament, physical_address_location });
                }} />
            </FormControl>
            <FormControl isFieldset fill label={labels.password_protection}>
                <TextInput fill defaultValue={tournament.password} onInputComplete={(password) => {
                    setTournament({ ...tournament, password });
                }} />
            </FormControl>
            <Checkbox label={labels.invite_only} defaultChecked={tournament.details_settings?.invite_only} onChecked={(invite_only) => {
                setTournament({ ...tournament, details_settings: { ...tournament.details_settings as any, invite_only } });
            }} />
            <Checkbox label={labels.publish_as_template} defaultChecked={tournament.is_template} onChecked={(is_template) => {
                setTournament({ ...tournament, is_template });
            }} />
            <Checkbox label={labels.make_tournament_private} defaultChecked={tournament.details_settings?.is_private} onChecked={(is_private) => {
                setTournament({ ...tournament, details_settings: { ...tournament.details_settings as any, is_private } });
            }} />
        </Form>
        <div className="controls">
            <Button textOnly scheme={Scheme.DANGER} disabled={!tournament.external_id} text={i18nManager.Labels.common.cancel} onClick={cancelCreateTournament} />
            <Button loadingProps={Elements.BUTTON_LOADING_PROPS} scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.next} onClick={(e) => {
                e.manageRef?.setLoadingState(true);
                props.setActiveSection(CreateTournamentSection.PROGRAM_AND_PARTICIPANT, () => {
                    e.manageRef?.setLoadingState(false);
                });
            }} />
        </div>
    </div>);

    function fetchInformations() {
        countryService.queryCountries({ name: "" }).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
            setCountries(apiResponse.data!.content);
        }).catch(BaseService.reportError);
        queryTemplateTournaments("");
    }

    function queryTemplateTournaments(name: string = "") {
        tournamentService.queryTournamentEditions({ name, is_template: true, size: 15, __member_id__: profile.external_id, }).then(({ content = [] }: SanitizedResponse<ITournamentEdition>) => {
            const sameHostTournaments: ITournamentEdition[] = [];
            const otherHostTournaments: ITournamentEdition[] = [];
            for (const tour of content) {
                if (tour.tournament.host.public_id === props.hostPublicId) {
                    sameHostTournaments.push(tour);
                } else {
                    otherHostTournaments.push(tour);
                }
            }
            // dropdown option not updated cause the main two array content is not changing, check comonent did update method in dropdown componenr
            setTemplateTournaments([
                {
                    label: labels.from_same_host,
                    items: sameHostTournaments
                },
                {
                    label: labels.from_other_host,
                    items: otherHostTournaments
                },
            ]);
        }).catch(BaseService.reportError);
    }

    function cancelCreateTournament() {
        const dialogLabels = i18nManager.Labels.host.create_tournament.dialog.cancel;
        const dialog = ConfirmDialog({
            closable: true,
            desc: dialogLabels.desc,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                return false;
            }
        });
        dialog.show();
    }

}

export default Details;
